import { useContext } from "react";
import { useSelector } from "react-redux";
import { get } from "underscore";
import styled, { css } from "styled-components";
import { DetailsCellStyled } from "../../../components/shared/details-cells/DetailsCellStyled";
import COMMON_LOGS_DATA_MODEL from "../../../utils/constants/commonLogsDataModel";
import LOGBOOK_LABEL from "../../../utils/constants/logbookLabel";
import { valueOrEmpty } from "../../../utils/helpers/text";
import { formatUtcToLocal } from "../helpers";
import { InstrumentInformationsContext } from "../instrument-informations-dialog/instrument-informations-context/context";
import { ItemDialogFormContext } from "../log-book-item-form-dialog/item-context/context";
import ValueOrFallbackText from "./ValueOrFallbackText";
import { LOG_ENTRY_TYPE } from "../../../constants";
import { EN_DASH } from "@digitallab/grid-common-frontend-components";
import { OwcChip, OwcIcon } from "@one/react";
import ClusterLogBookSubComponentActions from "../log-book-table/ClusterLogBookSubComponentActions";
export const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

export const ClickableCell = styled.span`
  :hover {
    color: #0066cc;
  }
  cursor: pointer;
`;

export const CustomDetailsCellStyled = styled(DetailsCellStyled)`
  ${(props) =>
    props.expand &&
    css`
      &&& {
        width: auto;
      }

      & > :last-child {
        text-overflow: unset;
        overflow: unset;
        white-space: pre-wrap;
        word-break: break-word;
        overflow-wrap: anywhere;
      }
    `}
`;

export const RunLogWrapper = styled.div`
  ${DetailsCellStyled} {
    display: inline-block;
    margin-right: 40px;
    margin-bottom: 20px;
    width: 190px;
  }
`;

export const EquipmentIdColumn = {
  equipmentId: {
    text: COMMON_LOGS_DATA_MODEL.equipmentId.value,
    component: ({ item }) => valueOrEmpty(item?.equipmentId, false, "-"),
    headProps: {
      style: {
        flex: "0 1 12%"
      }
    },
    cellProps: {
      style: {
        flex: "0 1 12%"
      }
    }
  }
};

export const SerialNumberColumn = {
  serialNumber: {
    text: COMMON_LOGS_DATA_MODEL.serialNumber.value,
    component: ({ item }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { handleOpenInstrumentInformation } = useContext(InstrumentInformationsContext);

      return (
        <ClickableCell onClick={() => handleOpenInstrumentInformation(item)}>
          {valueOrEmpty(item?.serialNumber, false, "-")}
        </ClickableCell>
      );
    },
    headProps: {
      style: {
        flex: "0 1 13%"
      }
    },
    cellProps: {
      style: {
        flex: "0 1 13%"
      }
    }
  }
};

export const InstrumentModelColumn = {
  equipmentModel: {
    text: COMMON_LOGS_DATA_MODEL.equipmentModel.value,
    component: ({ item }) => valueOrEmpty(item?.equipmentModel, false, "-"),
    headProps: {
      style: {
        flex: "0 1 15%"
      }
    },
    cellProps: {
      style: {
        flex: "0 1 15%"
      }
    }
  }
};

export const EquipmentNickNameColumn = {
  equipmentNickName: {
    text: COMMON_LOGS_DATA_MODEL.equipmentNickName.value,
    component: ({ item }) => valueOrEmpty(item?.instrumentDetails?.equipmentNickName, false, "-"),
    headProps: {
      style: {
        flex: "0 1 15%"
      }
    },
    cellProps: {
      style: {
        flex: "0 1 15%"
      }
    }
  }
};

export const ApproverUserName = {
  approverUserName: {
    text: COMMON_LOGS_DATA_MODEL.approverUserName.value,
    headProps: {
      style: {
        flex: "0 1 15%"
      }
    },
    cellProps: {
      style: {
        flex: "0 1 15%"
      }
    }
  }
};

export const VariantColumn = {
  variant: {
    text: COMMON_LOGS_DATA_MODEL.variant.value,
    headProps: {
      style: {
        flex: "0 1 15%"
      }
    },
    cellProps: {
      style: {
        flex: "0 1 15%"
      }
    }
  }
};

/**
 * Function to convert any string to simple, only alphanumerical string.
 * @param string String to convert
 */
export const toSimpleString = (string) => {
  return string?.toLowerCase?.()?.replaceAll?.(/[\W_]/g, "");
};

/**
 * Function to compare values of action type from booking and log book.
 * @param {*} logBookValue option from log book values
 * @param {*} bookingValue option from booking values
 * @return {boolean}
 */
export const actionTypeValuesComparator = (logBookValue, bookingValue) => {
  return (
    toSimpleString(logBookValue.key) === toSimpleString(bookingValue.key) ||
    toSimpleString(logBookValue.key) === toSimpleString(bookingValue.key.split("_").slice(1).join())
  );
};

export const ActionTypeCell = ({ item }) => (
  <ValueOrFallbackText
    reduxPath={["runLogsForm", "actionLog"]}
    comparator={actionTypeValuesComparator}
    item={item.action}
    defaultValue="-"
  />
);
export const GxpReadyCell = ({ item }) => {
  return (
    <>
      <ValueOrFallbackText
        reduxPath={["runLogsForm", "gxpReadys"]}
        item={produceValueForItem(item, "gxpReady")}
        defaultValue="-"
      />
    </>
  );
};

export const produceValueForItem = (item, name) => {
  return item[name]?.newValue ?? item[name];
};

const EMPTY_SYSTEM_STATUS_TOKENS = ["", "-", EN_DASH, "null"];

export const isEmptySystemStatus = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && EMPTY_SYSTEM_STATUS_TOKENS.includes(value.trim()));

export const resolveSystemStatusText = (rawValue, systemStatuses = []) => {
  if (isEmptySystemStatus(rawValue)) {
    return "";
  }
  if (typeof rawValue === "string") {
    return rawValue.trim();
  }
  const matched = (systemStatuses ?? []).find((status) => status?.key === rawValue?.key);
  return matched?.value || rawValue?.value || "";
};

export const SystemStatusCell = ({ item, isAuditPage = false }) => {
  const systemStatuses = useSelector((state) => get(state, ["runLogsForm", "systemStatuses"]));
  const rawValue = produceValueForItem(item, "systemStatus");
  const resolvedText = resolveSystemStatusText(rawValue, systemStatuses);

  if (isEmptySystemStatus(resolvedText)) {
    return EN_DASH;
  }

  if (isAuditPage) {
    return resolvedText;
  }

  return <OwcChip outlined>{resolvedText}</OwcChip>;
};

export const ApprovalStatusCell = ({ item }) => {
  if (item.isApproved) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <OwcIcon name="certificate" family="outlined"></OwcIcon>
        Approved
      </div>
    );
  }

  return "-";
};

export const GxpReadyAllCell = ({ item }) => {
  const textValue = valueOrEmpty(produceValueForItem(item, "gxpReady"), false, "-");
  const displayText = textValue.value ?? (typeof textValue === "string" ? textValue : "-");
  return <OwcChip outlined>{displayText}</OwcChip>;
};

export const SystemStatusAllCell = ({ item }) => {
  const systemStatuses = useSelector((state) => get(state, ["runLogsForm", "systemStatuses"]));
  const rawValue = produceValueForItem(item, "systemStatus");
  const resolvedText = resolveSystemStatusText(rawValue, systemStatuses);

  if (isEmptySystemStatus(resolvedText)) {
    return EN_DASH;
  }

  return <OwcChip outlined>{resolvedText}</OwcChip>;
};

/**
 * Factory function to create CreatedAt cell components with different date/time variants
 * @param {'date' | 'time' | 'datetime'} variant - The format variant to display
 */
const createCreatedAtCell =
  (variant) =>
  ({ item }) =>
    item?.createdAt ? formatUtcToLocal(item.createdAt, { variant }) : "-";

/** Cell component for displaying Creation Date (date only) - Used in table columns */
export const CreatedAtCell = createCreatedAtCell("date");

/** Cell component for displaying Creation Time - Used in expanded views */
export const CreatedTimeAtCell = createCreatedAtCell("time");

/**
 * Cell component for displaying "Intended Use GxP Relevant"
 * Reads from equipmentDetails snapshot
 */
export const GxpRelevantCell = ({ item }) => valueOrEmpty(item?.equipmentDetails?.gxpRelevant, false, "-");

/**
 * Extracts configuration baseline value from item
 * Checks logbookConfigurationBaseline first, falls back to instrumentDetails
 */
const getConfigurationBaseline = (item) =>
  item?.logbookConfigurationBaseline?.newValue;

/** Cell component for displaying Configuration Baseline - For Action Logs only */
export const ConfigurationBaselineCell = ({ item }) => valueOrEmpty(getConfigurationBaseline(item), false, "-");

/** Cell component for Configuration Baseline in All Logs view - Only shows for Action Logs */
export const ConfigurationBaselineAllLogCell = ({ item }) => {
  if (item?.logType === LOG_ENTRY_TYPE.ACTION) {
    return valueOrEmpty(getConfigurationBaseline(item), false, "-");
  }
  return "-";
};

export const MediaTypeCell = ({ item }) => (
  <ValueOrFallbackText
    reduxPath={["runLogsForm", "mediaTypes"]}
    item={item.mediaType}
    defaultValue="-"
    isArrayOfObjects={true}
  />
);

export const InstrumentModelClusterColumn = {
  equipmentModel: {
    text: COMMON_LOGS_DATA_MODEL.equipmentModel.value,
    component: ({ item }) => valueOrEmpty(item?.equipmentModel, false, "-"),
    headProps: {
      style: {
        flex: "0 1 19%"
      }
    },
    cellProps: {
      style: {
        flex: "0 1 19%"
      }
    }
  }
};

export const EquipmentNickNameClusterColumn = {
  equipmentNickName: {
    text: COMMON_LOGS_DATA_MODEL.equipmentNickName.value,
    component: ({ item }) => valueOrEmpty(item?.instrumentDetails?.equipmentNickName, false, "-"),
    headProps: {
      style: {
        flex: "0 1 16%"
      }
    },
    cellProps: {
      style: {
        flex: "0 1 16%"
      }
    }
  }
};

export const ApproverUserNameCluster = {
  approverUserName: {
    text: COMMON_LOGS_DATA_MODEL.approverUserName.value,
    headProps: {
      style: {
        flex: "0 1 17%"
      }
    },
    cellProps: {
      style: {
        flex: "0 1 17%"
      }
    }
  }
};

export const InstrumentClusterSubComponentColumn = (dataTestId) => ({
  subEquipment: {
    component: ({ item }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { setSubComponentOpen } = useContext(ItemDialogFormContext);
      return (
        <ClusterLogBookSubComponentActions
          dataTestId={dataTestId}
          item={item}
          setSubComponentOpen={setSubComponentOpen}
        />
      );
    },
    text: LOGBOOK_LABEL.sub_components,
    headProps: {
      style: {
        flex: "0 0 132px"
      }
    },
    cellProps: {
      style: {
        flex: "0 0 132px"
      }
    }
  }
});
